import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle,
  Phone,
  Mail,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  BellOff,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const STATUS_COLORS = {
  lead: 'bg-blue-100 text-blue-700',
  qualified: 'bg-cyan-100 text-cyan-700',
  quote_sent: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-emerald-100 text-emerald-700',
  deposit_taken: 'bg-teal-100 text-teal-700',
  scheduled: 'bg-lime-100 text-lime-700',
  ordered: 'bg-yellow-100 text-yellow-700',
  in_progress_installation: 'bg-orange-100 text-orange-700',
  in_progress_invoiced: 'bg-amber-100 text-amber-700',
  in_progress_handover: 'bg-rose-100 text-rose-700',
};

export const StaleCustomersAlert = () => {
  const navigate = useNavigate();
  const [staleCustomers, setStaleCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const handleSnooze = async (e, customerId, days) => {
    e.stopPropagation();
    try {
      await api.post(`/customers/${customerId}/snooze`, { days });
      toast.success(`Snoozed for ${days} day${days > 1 ? 's' : ''}`);
      fetchStaleCustomers();
    } catch (error) {
      toast.error('Failed to snooze customer');
    }
  };

  const fetchStaleCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customers/stale');
      setStaleCustomers(response.data.slice(0, 5)); // Show top 5
    } catch (error) {
      console.error('Failed to fetch stale customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaleCustomers();
  }, []);

  if (loading) {
    return (
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (staleCustomers.length === 0) {
    return null; // Don't show if no stale customers
  }

  return (
    <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Customers Requiring Attention
            <Badge className="bg-amber-100 text-amber-700 ml-2">
              {staleCustomers.length}+
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchStaleCustomers}
              className="text-amber-700 hover:text-amber-800"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed(!collapsed)}
              className="text-amber-700 hover:text-amber-800"
            >
              {collapsed ? 'Show' : 'Hide'}
            </Button>
          </div>
        </CardTitle>
        <p className="text-sm text-amber-700">
          These customers have been in the same status for over 2 weeks
        </p>
      </CardHeader>
      
      {!collapsed && (
        <CardContent className="pt-0">
          <div className="space-y-3">
            {staleCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/customers/${customer.id}`)}
                data-testid={`stale-customer-${customer.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">
                      {customer.first_name} {customer.last_name}
                    </span>
                    <Badge className={STATUS_COLORS[customer.status] || 'bg-slate-100 text-slate-700'}>
                      {customer.status_display}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {customer.days_stale} days
                    </span>
                    {customer.phone && (
                      <a 
                        href={`tel:${customer.phone}`} 
                        className="flex items-center gap-1 hover:text-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-3 h-3" />
                        {customer.phone}
                      </a>
                    )}
                    {customer.email && (
                      <a 
                        href={`mailto:${customer.email}`}
                        className="flex items-center gap-1 hover:text-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="w-3 h-3" />
                        Contact
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-slate-500 hover:text-amber-600"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`snooze-btn-${customer.id}`}
                        title="Snooze"
                      >
                        <BellOff className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => handleSnooze(e, customer.id, 1)} data-testid={`snooze-1d-${customer.id}`}>
                        Snooze 1 day
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleSnooze(e, customer.id, 2)} data-testid={`snooze-2d-${customer.id}`}>
                        Snooze 2 days
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleSnooze(e, customer.id, 3)} data-testid={`snooze-3d-${customer.id}`}>
                        Snooze 3 days
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
          
          <Button
            variant="outline"
            className="w-full mt-4 border-amber-200 text-amber-700 hover:bg-amber-50"
            onClick={() => navigate('/customers?status=all')}
          >
            View All Customers
          </Button>
        </CardContent>
      )}
    </Card>
  );
};
